import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MainContainer } from "../../../components/ContentWrapper";
import { WhiteBackground } from "../../../components/WhiteBackground";
import { ListPageHeader } from "../../../components/ListPageHeader/ListPageHeader";
import { DataTable, DataTableColumn } from "../../../components/DataTable/DataTable";
import { UsagePeriodPicker, useUsagePeriod } from "./UsagePeriodPicker";
import { useCommonParams } from "../../../hooks/useCommonParams";
import { useResourceList } from "../../../hooks/useResourceList";
import { adminBrandsUsageEndpoint } from "../../../api/endpoints";
import { formatCurrency } from "../../../utils/formatCurrency";
import { initialsFromName } from "../../../utils";
import { formatProductCap, intervalLabel, planLabel } from "../../../utils/plans";
import type { BrandUsageResponse, BrandUsageRow, UsageLine } from "../../../types/pricingPlans";

// Per-brand usage for a period: real counts per line (PDP audits, catalog
// reports, prep packs), our recorded LLM cost, the rate-card modeled cost,
// and the customer price (count × unit price). Admin-only; costs never
// reach the brand portal.
const PAGE_SIZE = 25;


const num = (n: number) => n.toLocaleString("en-US");

const costCell = (n: number) => (n === 0 ? "—" : formatCurrency(n));

export default function BrandUsagePage() {
    const navigate = useNavigate();
    const { searchParams, setSearchParams, page, setPage, searchText, setSearchText } = useCommonParams();
    const { extraParams } = useUsagePeriod();
    const [meta, setMeta] = useState<Pick<BrandUsageResponse, "grand_totals" | "unattributed" | "rates"> | null>(
        null
    );

    const list = useResourceList<BrandUsageRow>({
        endpoint: adminBrandsUsageEndpoint,
        pageSize: PAGE_SIZE,
        searchText,
        searchParams,
        setSearchParams,
        page,
        setPage,
        extraParams,
        errorLabel: "Failed to load brand usage",
        onResponse: (data: BrandUsageResponse) =>
            setMeta({ grand_totals: data.grand_totals, unattributed: data.unattributed, rates: data.rates }),
    });

    // A kind the API predates (or an older cached payload) reads as zero.
    const line = (r: BrandUsageRow, kind: keyof BrandUsageRow["lines"]): UsageLine =>
        r.lines[kind] || { count: 0, cached: 0, actual_cost: 0, modeled_cost: 0, price: 0 };

    const columns: DataTableColumn<BrandUsageRow>[] = [
        {
            key: "brand",
            header: "Brand",
            render: (r) => (
                <div className="flex items-center gap-[10px]">
                    <div className="w-[30px] h-[30px] rounded-[7px] bg-tint text-accent-text text-[11px] font-bold flex items-center justify-center shrink-0 overflow-hidden">
                        {r.brand.logo ? (
                            <img src={r.brand.logo} alt="" className="w-full h-full object-cover" />
                        ) : (
                            initialsFromName(r.brand.name)
                        )}
                    </div>
                    <div className="min-w-0">
                        <div className="font-semibold text-text-1 truncate">{r.brand.name}</div>
                        {r.brand.slug && <div className="text-[11.5px] text-text-2 truncate">/{r.brand.slug}</div>}
                    </div>
                </div>
            ),
        },
        {
            key: "plan",
            header: "Plan",
            render: (r) => (
                <span
                    className={
                        r.is_network
                            ? "font-bold uppercase border border-[#2474DD] bg-surface text-[#2474DD] text-[10.5px] rounded-md px-1.5 py-0.5"
                            : "font-bold uppercase border border-border-token bg-surface-2 text-text-2 text-[10.5px] rounded-md px-1.5 py-0.5"
                    }
                >
                    {planLabel(r.plan)}
                </span>
            ),
        },
        {
            key: "products",
            header: "Scoring",
            className: "text-right font-mono",
            render: (r) => (
                <span
                    className={
                        r.max_products !== null && r.product_count > r.max_products ? "text-down font-semibold" : ""
                    }
                >
                    {num(r.product_count)} / {formatProductCap(r.max_products)}
                </span>
            ),
        },
        {
            key: "cadence",
            header: "Max cadence",
            render: (r) => intervalLabel(r.max_pdp_score_interval),
            className: "text-text-2 text-[12px]",
        },
        {
            key: "audits",
            header: "Audits",
            className: "text-right font-mono",
            render: (r) => num(line(r, "audit").count),
        },
        {
            key: "reports",
            header: "Reports",
            className: "text-right font-mono",
            render: (r) => num(line(r, "catalog_report").count),
        },
        {
            key: "prep_packs",
            header: "Prep packs",
            className: "text-right font-mono",
            render: (r) => num(line(r, "prep_pack").count),
        },
        {
            key: "consumer_voice",
            header: "Consumer Voice",
            className: "text-right font-mono",
            render: (r) => num(line(r, "consumer_voice").count + line(r, "consumer_voice_report").count),
        },
        {
            key: "ai_presence",
            header: "AI Presence",
            className: "text-right font-mono",
            render: (r) => num(line(r, "ai_presence").count + line(r, "ai_presence_standalone").count),
        },
        {
            key: "actual_cost",
            header: "Our cost",
            className: "text-right font-mono",
            render: (r) => (
                <span title={`Recorded cost (AI + scraping). Audits ${formatCurrency(line(r, "audit").actual_cost)} · Reports ${formatCurrency(line(r, "catalog_report").actual_cost)} · Prep packs ${formatCurrency(line(r, "prep_pack").actual_cost)} · Consumer Voice ${formatCurrency(line(r, "consumer_voice").actual_cost + line(r, "consumer_voice_report").actual_cost)} · AI Presence ${formatCurrency(line(r, "ai_presence").actual_cost + line(r, "ai_presence_standalone").actual_cost)}`}>
                    {costCell(r.totals.actual_cost)}
                </span>
            ),
        },
        {
            key: "modeled_cost",
            header: "Modeled cost",
            className: "text-right font-mono text-text-2",
            render: (r) => costCell(r.totals.modeled_cost),
        },
        {
            key: "price",
            header: "Customer price",
            className: "text-right font-mono font-semibold",
            render: (r) => (
                <span title={`Audits ${formatCurrency(line(r, "audit").price)} · Reports ${formatCurrency(line(r, "catalog_report").price)} · Prep packs ${formatCurrency(line(r, "prep_pack").price)} · Consumer Voice ${formatCurrency(line(r, "consumer_voice").price + line(r, "consumer_voice_report").price)} · AI Presence ${formatCurrency(line(r, "ai_presence").price + line(r, "ai_presence_standalone").price)}`}>
                    {costCell(r.totals.price)}
                </span>
            ),
        },
    ];

    const gt = meta?.grand_totals;
    const footerCells: React.ReactNode[] | undefined = gt
        ? [
              <span key="label">All brands ({num(list.totalCount)})</span>,
              null,
              null,
              null,
              num(gt.lines.audit.count),
              num(gt.lines.catalog_report.count),
              num(gt.lines.prep_pack.count),
              costCell(gt.totals.actual_cost),
              costCell(gt.totals.modeled_cost),
              costCell(gt.totals.price),
          ]
        : undefined;

    const rateSummary = meta?.rates
        ? `Rates: audit ${formatCurrency(meta.rates.audit.unit_price)} · report ${formatCurrency(
              meta.rates.catalog_report.unit_price
          )} · prep pack ${formatCurrency(meta.rates.prep_pack.unit_price)}`
        : "";
    const unattributed = meta?.unattributed?.prep_pack;

    return (
        <WhiteBackground>
            <MainContainer>
                <ListPageHeader
                    title="Brand Usage"
                    subtitle="PDP audits, catalog reports and prep packs per brand — our cost vs. customer price"
                    searchValue={searchText}
                    onSearchChange={setSearchText}
                    searchPlaceholder="Search brands"
                    searchDebounceMs={300}
                    searchRightContent={<UsagePeriodPicker />                    }
                />
                {(rateSummary || unattributed) && (
                    <div className="text-[12px] text-text-2 pb-3 flex flex-wrap gap-x-4 gap-y-1">
                        {rateSummary && <span>{rateSummary}</span>}
                        {unattributed && unattributed.count > 0 && (
                            <span>
                                Unattributed prep packs (no brand): {num(unattributed.count)} ·{" "}
                                {formatCurrency(unattributed.actual_cost)} cost
                            </span>
                        )}
                    </div>
                )}
                <DataTable<BrandUsageRow>
                    columns={columns}
                    rows={list.items}
                    loading={list.loading}
                    totalCount={list.totalCount}
                    hasMore={list.hasMore}
                    error={list.error}
                    onLoadMore={list.loadMore}
                    emptyMessage="No usage in this period"
                    getRowKey={(r) => r.brand.id}
                    onRowClick={(r) => navigate(`/admin/brands/${r.brand.id}/usage`)}
                    footerCells={footerCells}
                />
            </MainContainer>
        </WhiteBackground>
    );
}
